import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserTickets.css";

export default function UserTickets() {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    location: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = (localStorage.getItem("role") || "").toLowerCase();

    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    if (role !== "user") {
      navigate("/ticket-manager", { replace: true });
    }

    fetchTickets();
  }, [navigate]);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/ticket/getAll`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      setTickets(data || []);
    } catch (err) {
      console.error("Failed to load tickets", err);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();

    if (
      !newTicket.title.trim() ||
      !newTicket.description.trim() ||
      !newTicket.location.trim()
    ) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ticket/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newTicket),
      });

      const data = await res.json();

      setTickets((prev) => [data, ...prev]);

      setNewTicket({ title: "", description: "", location: "" });
      setShowCreateModal(false);
    } catch (err) {
      console.error("Create ticket failed", err);
    }
  };

  const openDetails = (ticket) => {
    setSelectedTicket(ticket);
    setNewMessage("");
  };

  const closeDetails = () => {
    setSelectedTicket(null);
  };

  const addMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ticket/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: selectedTicket._id,
          message: newMessage,
        }),
      });

      const updatedTicket = await res.json();

      setTickets((prev) => prev.map((t) => t._id === updatedTicket._id ? updatedTicket : t));

      setSelectedTicket(updatedTicket);

      setNewMessage("");
    } catch (err) {
      console.error("Message send failed", err);
    }
  };

  const changeStatus = async (e) => {
    const status = e.target.value;

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/ticket/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: selectedTicket._id,
          status,
        }),
      });

      const updatedTicket = await res.json();

      setTickets((prev) => prev.map((t) => t._id === updatedTicket._id ? updatedTicket : t));

      setSelectedTicket(updatedTicket);
    } catch (err) {
      console.error("Status update failed", err);
    }
  };

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        <h2 className="tickets-title">My Tickets</h2>
        <button
          className="btn btn-primary create-ticket-btn"
          onClick={() => setShowCreateModal(true)}
        >
          Create Ticket
        </button>
      </div>

      <div className="tickets-list">
        {tickets?.map((ticket) => (
          <div key={ticket._id} className="ticket-card">
            <h3 className="ticket-title">{ticket.title}</h3>

            <p className="ticket-description">{ticket.description}</p>

            <p className="ticket-location">
              <strong>Location:</strong> {ticket.location}
            </p>

            <p className="ticket-status">
              <strong>Status:</strong> {ticket.status}
            </p>

            <button
              className="btn btn-secondary view-ticket-btn"
              onClick={() => openDetails(ticket)}
            >
              View Details
            </button>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal create-ticket-modal">
            <h3 className="modal-title">Create Ticket</h3>

            <form className="ticket-form" onSubmit={handleCreateTicket}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  className="form-input"
                  type="text"
                  value={newTicket.title}
                  onChange={(e) =>
                    setNewTicket((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={newTicket.description}
                  onChange={(e) =>
                    setNewTicket((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">Location</label>
                <textarea
                  className="form-textarea"
                  value={newTicket.location}
                  onChange={(e) =>
                    setNewTicket((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" type="submit">
                  Submit Ticket
                </button>

                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="modal-overlay">
          <div className="modal ticket-details-modal">
            <h3 className="ticket-details-title">{selectedTicket.title}</h3>

            <p className="ticket-details-description">
              {selectedTicket.description}
            </p>

            <p className="ticket-details-location">
              <strong>Location:</strong> {selectedTicket.location}
            </p>

            <p className="ticket-details-status">
              <strong>Status:</strong> {selectedTicket.status}
            </p>

            <h4 className="messages-title">Messages</h4>

            <div className="messages-list">
              {selectedTicket.messages?.map((msg, index) => (
                <div key={index} className="message-item">
                  <div className="message-header">
                    <strong className="message-author">
                      {msg.authorName}
                    </strong>
                    <small className="message-time">
                      {new Date(msg.createdAt).toLocaleString([],{
                        dateStyle: "medium", timeStyle: "short"
                      } )}
                    </small>
                  </div>

                  <p className="message-text">{msg.text}</p>
                </div>
              ))}
            </div>

            <textarea
              className="message-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Add message..."
            />

            <div className="message-actions">
              <button className="btn btn-primary" onClick={addMessage}>
                Send Message
              </button>

              <button className="btn btn-secondary" onClick={closeDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
